<template>
	<div v-if="isFlatMenu">
		<div v-if="processWorkflowItems?.length && !hideProcessWorkflows">
			<SubMenuTitle title="PROCESS MODULES" />
			<MenuListItem
				v-for="(item, index) in processWorkflowItems"
				v-on="$listeners"
				class="mb-1"
				:item="item"
				:index="index"
				:key="item.key + index"
				:focusedMenuItemKey="focusedMenuItemKey"
				@select-item="selectProcessWorkflow"
			/>
		</div>
		<div v-if="configurationItems.length">
			<MenuListItem
				v-for="(item, index) in configurationItems"
				v-on="$listeners"
				class="mb-1"
				:addIndex="false"
				:item="item"
				:index="index"
				:key="item.key + index"
				:disabled="isItemDisabled(item)"
				:focusedMenuItemKey="focusedMenuItemKey"
				:isItemTestedSuccessfully="isStepTestedSuccessfully(item)"
				:disabledBecauseNoTable="doesItemNeedAndDoesNotHaveTable(item) && isStepTestedSuccessfully(item)"
				@select-item="selectItem"
				@focus-on-item="focusOnItem"
			/>
		</div>
		<SubMenuTitle :title="prevStepsSubMenuTitle" />
		<MenuListItem
			v-for="(item, index) in workflowStepsItems"
			v-on="$listeners"
			class="mb-1"
			:item="item"
			:index="index"
			:key="item.key + index"
			:disabled="isItemDisabled(item)"
			:focusedMenuItemKey="focusedMenuItemKey"
			:isItemTestedSuccessfully="isStepTestedSuccessfully(item)"
			:disabledBecauseNoTable="doesItemNeedAndDoesNotHaveTable(item) && isStepTestedSuccessfully(item)"
			@select-item="selectItem"
			@focus-on-item="focusOnItem"
		/>
	</div>
	<div v-else>
		<div v-for="(item, index) in relevantDataSourcesItems" :key="item.key + index">
			<StepExpandedData
				v-if="Object.keys(getFilteredItems(item) || {}).length > 0"
				v-on="$listeners"
				ref="step_expanded_data"
				:step="item"
				:index="index"
				:isTableOnly="isTableOnly"
				:searchString="searchString"
				:useWithFormulas="useWithFormulas"
				:contentRectYAxis="contentRectYAxis"
				:filteredItems="getFilteredItems(item)"
				:allowTableSelection="allowTableSelection"
				:focusedMenuItemKey="focusedMenuItemKey"
				:showItemHeader="showExpandedItemHeader"
				:didFocusFromBottomToTop="didFocusFromBottomToTop"
				:dataInputParam="dataInputParam"
				:preventTables="preventTables"
				@table-focus-exit-from-bottom="focusOnNextMenuItem(true)"
				@table-focus-exit-from-top="focusOnPreviousMenuItem(true)"
				@focus-on-item="focusOnItem"
			/>
		</div>
		<NoDataFoundMessage v-if="showNoDataFoundMessage" />
	</div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';

import { isArrayOfObjects } from '@/utils/helpers';

import { isStatusPublishValid } from '@/components/BlockEditor/block-editor-service';
import DataInputMenuService from '@/components/DataInput/DataInputMenu/data-input-menu-service';

import SubMenuTitle from '@/components/DataInput/DataInputMenu/SubMenuTitle';
import StepExpandedData from '@/components/DataInput/DataInputMenu/StepExpandedData';
import MenuListItem from '@/components/DataInput/DataInputMenu/MenuLists/MenuListItem';
import NoDataFoundMessage from '@/components/DataInput/DataInputMenu/NoDataFoundMessage';
import DataInputMenuItemBaseMixin from '@/components/DataInput/DataInputMenu/MenuLists/helpers/DataInputMenuItemBaseMixin';
// import * as WorkflowNodeService from '@/utils/workflow-node-service'; return this when returning filtering tables

export default {
	name: 'PreviousStepsData',
	mixins: [DataInputMenuItemBaseMixin],
	components: {
		SubMenuTitle,
		MenuListItem,
		StepExpandedData,
		NoDataFoundMessage,
	},
	props: {
		isTableOnly: { type: Boolean },
		errorMessage: { type: String },
		searchString: { type: String },
		dataSourcesItems: { type: Array },
		contentRectYAxis: { type: Number },
		useWithFormulas: { type: Boolean },
		preventTables: { type: Boolean },
		allowTableSelection: { type: Boolean },
		dataInputParam: { type: Object },
		expandedItemId: { type: [String, Number, null] },
		hideProcessWorkflows: { type: Boolean },
		processWorkflowItems: { type: Array },
		prevStepsSubMenuTitle: { type: String, default: 'PREVIOUS STEPS' },
	},
	computed: {
		showNoDataFoundMessage() {
			const temp = this.relevantDataSourcesItems.map((step) => this.getFilteredItems(step));
			const isNoResults = temp.every((filteredItems) => isEmpty(filteredItems));
			return isNoResults;
		},
		showExpandedItemHeader() {
			return Boolean(this.expandedItem);
		},
		workflowStepsItems() {
			return this.dataSourcesItems.filter((item) => !item.isGlobal && !item.isConfiguration);
		},
		configurationItems() {
			return this.dataSourcesItems.filter((item) => item.isGlobal || item.isConfiguration);
		},
		isFlatMenu() {
			return !(this.searchString?.length > 0 || this.expandedItemId);
		},
		expandedItem() {
			if (!this.expandedItemId || !this.localDataSourceItems.length > 0) {
				return null;
			}
			return this.localDataSourceItems.find((item) => item.key === this.expandedItemId);
		},
		isFocusOnTableItem() {
			return this.isTable(this.focusedItem);
		},
		expandedItemNodeInfo() {
			return this.expandedItem?.nodeInfo;
		},
		expandedItemNodeServiceSlug() {
			return this.expandedItemNodeInfo?.serviceSlug;
		},
		expandedItemNodeTitle() {
			return this.expandedItemNodeInfo?.nodeTitle;
		},
		relevantDataSourcesItems() {
			if (this.expandedItem) {
				return [this.expandedItem];
			} else {
				return this.localDataSourceItems;
			}
		},
		focusedMenuItemIndexOnDisplay() {
			return this.allMenuItemsKeysOnDisplay.findIndex((key) => key === this.focusedMenuItemKey);
		},
		focusedItem() {
			const stepKey = this.focusedMenuItemKey?.split('_menuItemUniqueKey_')[0];
			const parsedKey = !isNaN(stepKey) ? Number(stepKey) : stepKey; //if key is a string that is not a number, leave it as is, if its a number in type string, convert to number
			const selectedItemKey = this.focusedMenuItemKey?.split('_menuItemUniqueKey_')[1];
			const selectedItemStep = this.relevantDataSourcesItems.find((step) => step.key === parsedKey);
			return selectedItemStep?.items[selectedItemKey];
		},
		allMenuItemsKeysOnDisplay() {
			const allItemsKeysOnDisplay = [];
			if (this.isFlatMenu) {
				// case for 'flat menu'
				if (this.localDataSourceItems?.length > 0) {
					return this.localDataSourceItems
						.filter((item) => !this.isItemDisabled(item)) //leave out disableds
						.map((item) => DataInputMenuService.getMenuItemUniqueKey(item));
				}
			} else if (this.relevantDataSourcesItems?.length > 0) {
				// case for 'expanded steps menu'
				this.relevantDataSourcesItems.map((step) => {
					const allStepFilteredItems = this.getFilteredItems(step) || {};
					const allStepFilteredItemsKeys = Object.keys(allStepFilteredItems);
					if (allStepFilteredItemsKeys.length > 0) {
						allStepFilteredItemsKeys
							.filter((filteredItemKey) => !this.isDataItemDisabled(allStepFilteredItems[filteredItemKey])) // only active ones (not disabled)
							.map((filteredItemKey) => allItemsKeysOnDisplay.push(DataInputMenuService.getMenuItemUniqueKey(step, filteredItemKey)));
					}
				});
			}
			return allItemsKeysOnDisplay;
		},
		localDataSourceItems() {
			return this.dataSourcesItems;
		},
	},
	methods: {
		focusOnItem(menuItemUniqueKey) {
			this.focusedMenuItemKey = menuItemUniqueKey;
		},
		expandItem() {
			// Called from Parent
			if (!this.focusedMenuItemKey || !this.isFlatMenu) {
				return;
			}
			const stepKey = this.focusedMenuItemKey.split('_menuItemUniqueKey_')[0];
			const parsedKey = !isNaN(stepKey) ? Number(stepKey) : stepKey; //if key is a string that is not a number, leave it as is, if its a number in type string, convert to number
			this.selectItem(parsedKey);
		},
		doesItemContainTableSource(item) {
			const menuItemDataItems = this.getFilteredItems(item);
			const allItemValues = Object.values(menuItemDataItems);
			return allItemValues.some((dataItem) => Array.isArray(dataItem));
		},
		doesItemNeedAndDoesNotHaveTable(item) {
			return this.isTableOnly && !this.doesItemContainTableSource(item);
		},
		isTable(value) {
			return Array.isArray(value) || isArrayOfObjects(value);
		},
		isDataItemDisabled(dataItem) {
			return this.isTableOnly && !this.isTable(dataItem);
		},
		isItemDisabled(item) {
			return !this.isStepTestedSuccessfully(item) || this.doesItemNeedAndDoesNotHaveTable(item);
		},
		isStepTestedSuccessfully(node) {
			return isStatusPublishValid(node.nodeInfo?.testResultStatus);
		},
		unExpandItem() {
			// Called from Parent
			this.$emit('set-expanded-item-id', null);
		},
		onInputBlur() {
			// Called from Parent
			this.focusedMenuItemKey = null;
		},
		selectItem(itemId) {
			this.$emit('set-expanded-item-id', itemId);
		},
		triggerChildrenFunc(funcName) {
			this.$refs.step_expanded_data.map((stepRef) => {
				stepRef[funcName] && stepRef[funcName]();
			});
		},
		isNavigatingInsideTable(skipTable) {
			return this.isFocusOnTableItem && !skipTable && !this.isTableOnly && !this.allowTableSelection;
		},
		focusOnNextMenuItem(skipTable) {
			// Called from Parent
			if (this.isNavigatingInsideTable(skipTable)) {
				this.triggerChildrenFunc('focusOnNextMenuItem');
				return;
			}
			if (this.allMenuItemsKeysOnDisplay.length === 0) {
				return;
			}

			const currentFocusedIndex = this.focusedMenuItemIndexOnDisplay;
			const didGetToBottomOfTheList = this.allMenuItemsKeysOnDisplay.length <= currentFocusedIndex + 1;
			const upNextItemIndexToFocus = didGetToBottomOfTheList ? 0 : currentFocusedIndex + 1;
			if (didGetToBottomOfTheList) {
				this.didFocusFromBottomToTop = false;
			}
			this.didFocusFromBottomToTop = didGetToBottomOfTheList;
			this.focusedMenuItemKey = this.allMenuItemsKeysOnDisplay[upNextItemIndexToFocus];
		},
		focusOnPreviousMenuItem(skipTable) {
			// Called from Parent
			if (this.isNavigatingInsideTable(skipTable)) {
				this.triggerChildrenFunc('focusOnPreviousMenuItem');
				return;
			}
			if (this.allMenuItemsKeysOnDisplay.length === 0) {
				return;
			}
			const currentFocusedIndex = this.focusedMenuItemIndexOnDisplay;
			const didGetToTopOfTheList = currentFocusedIndex - 1 < 0;
			const upNextItemIndexToFocus = didGetToTopOfTheList ? this.allMenuItemsKeysOnDisplay.length - 1 : currentFocusedIndex - 1;
			this.didFocusFromBottomToTop = true;
			this.focusedMenuItemKey = this.allMenuItemsKeysOnDisplay[upNextItemIndexToFocus];
		},
		selectFocusedItem() {
			// Called from Parent
			if ((this.isFocusOnTableItem && !this.isTableOnly) || (this.dataInputParam?.format && this.expandedItem)) {
				this.triggerChildrenFunc('selectFocusedItem');
				return;
			}
			if (!this.focusedMenuItemKey) {
				return;
			}
			const stepKey = this.focusedMenuItemKey.split('_menuItemUniqueKey_')[0];
			const parsedKey = !isNaN(stepKey) ? Number(stepKey) : stepKey; //if key is a string that is not a number, leave it as is, if its a number in type string, convert to number
			if (this.isFlatMenu) {
				this.selectItem(parsedKey);
			} else {
				const selectedItemKey = this.focusedMenuItemKey.split('_menuItemUniqueKey_')[1];
				const selectedItemStep = this.relevantDataSourcesItems.find((step) => step.key === parsedKey);
				if (selectedItemKey && selectedItemStep) {
					this.$emit('set-data-input-menu-item', selectedItemStep, selectedItemKey);
				}
			}
		},
		getFilteredItems(step) {
			const { items } = step;
			const filterItems = (searchString) =>
				Object.fromEntries(
					Object.entries(items).filter(([k, v]) => {
						const key = this.getItemKeyTitle(step.key, k);
						const isTable = Array.isArray(v);
						if (isTable) {
							// if table always show, let the table representation handle what to show and how (except for the title which will always show)
							return true;
							// return this when we can understand what the display key of the table is
							// const nodeData = this.getNodeData(step.key);
							// const testResponseOutputLabels = WorkflowNodeService.getTestResponseOutputLabels(nodeData);
							// const tableValues = WorkflowNodeService.replaceTableValueWithOutputLabels(k, v, testResponseOutputLabels);
							// v = JSON.stringify(tableValues);
							// return String(v).toLowerCase().includes(searchString.toLowerCase());
						}
						return (
							String(v).toLowerCase().includes(searchString.toLowerCase()) || String(key).toLowerCase().includes(searchString.toLowerCase())
						);
					})
				);

			if (this.searchString) {
				return filterItems(this.searchString);
			} else {
				return items;
			}
		},
		isEmpty(value) {
			return isEmpty(value);
		},
		selectProcessWorkflow(itemId) {
			this.$emit('process-workflow-selected', itemId);
		},
	},
	data: () => ({
		blinkTimeout: null,
		blinkMessage: false,
		focusedMenuItemKey: null,
		didFocusFromBottomToTop: false,
	}),
	watch: {
		searchString() {
			this.focusedMenuItemKey = null;
		},
		isFlatMenu() {
			this.$emit('scroll-to-element', null, true);
			this.$emit('focus-on-search');
			this.focusedMenuItemKey = null;
			this.didFocusFromBottomToTop = false;
		},
	},
};
</script>

<style scoped lang="scss"></style>
